import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowRight, Home, Calendar, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function AbbonamentoSuccess() {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('order_id') || searchParams.get('codTrans');
  const [orderDetails, setOrderDetails] = useState(null);

  useEffect(() => {
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'instant' });
    
    // Track conversion event for analytics
    if (window.gtag) {
      window.gtag('event', 'purchase', {
        transaction_id: orderId,
        value: 0,
        currency: 'EUR',
        items: [{ name: 'Abbonamento La Villa' }]
      });
    }
    
    // Meta Pixel Purchase event
    if (window.fbq) {
      window.fbq('track', 'Purchase', {
        content_name: 'Abbonamento La Villa',
        content_type: 'subscription',
        order_id: orderId
      });
    }
  }, [orderId]);

  return (
    <div className="bg-ivory min-h-screen pt-32 pb-24" data-testid="success-page">
      <div className="max-w-2xl mx-auto px-6 text-center">
        {/* Success Animation */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", duration: 0.6 }}
          className="mb-8"
        >
          <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto">
            <CheckCircle className="w-16 h-16 text-green-600" />
          </div>
        </motion.div>

        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <h1 className="font-heading text-4xl md:text-5xl text-night mb-4">
            Acquisto Completato!
          </h1>
          <p className="font-body text-xl text-champagne mb-2">
            Grazie per aver scelto La Villa
          </p>
          {orderId && (
            <p className="font-body text-stone-500 text-sm">
              Ordine: <span className="font-mono font-bold">{orderId}</span>
            </p>
          )}
        </motion.div>

        {/* Info Box */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-2xl shadow-lg p-8 mt-10 text-left"
        >
          <h2 className="font-heading text-xl text-night mb-4">
            Cosa succede ora?
          </h2>
          <ul className="space-y-4 font-body text-stone-600">
            <li className="flex items-start gap-3">
              <div className="w-6 h-6 bg-champagne/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-champagne font-bold text-sm">1</span>
              </div>
              <div>
                <p className="font-medium text-night">Email di conferma</p>
                <p className="text-sm">Riceverai una email con i dettagli del tuo abbonamento.</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-6 h-6 bg-champagne/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-champagne font-bold text-sm">2</span>
              </div>
              <div>
                <p className="font-medium text-night">Accesso garantito</p>
                <p className="text-sm">Il tuo abbonamento è attivo per il periodo selezionato.</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-6 h-6 bg-champagne/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-champagne font-bold text-sm">3</span>
              </div>
              <div>
                <p className="font-medium text-night">Presentati all'ingresso</p>
                <p className="text-sm">Mostra la email di conferma o comunica il tuo nome all'ingresso.</p>
              </div>
            </li>
          </ul>
        </motion.div>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="mt-10 flex flex-col sm:flex-row gap-4 justify-center"
        >
          <Link to="/">
            <Button className="bg-night text-ivory hover:bg-stone-800 rounded-full px-8 py-6 text-sm uppercase tracking-widest group w-full sm:w-auto">
              <Home className="mr-2" size={16} />
              Torna alla Home
            </Button>
          </Link>
          <Link to="/night-events">
            <Button variant="outline" className="border-champagne text-champagne hover:bg-champagne hover:text-night rounded-full px-8 py-6 text-sm uppercase tracking-widest group w-full sm:w-auto">
              <Calendar className="mr-2" size={16} />
              Scopri gli Eventi
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={16} />
            </Button>
          </Link>
        </motion.div>

        {/* Footer Note */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9 }}
          className="mt-12 font-body text-sm text-stone-400"
        >
          Per qualsiasi domanda, contattaci su{' '}
          <a href="mailto:info@lavilla-castelfranco.it" className="text-champagne hover:underline">
            info@lavilla-castelfranco.it
          </a>
        </motion.p>
      </div>

      {/* Bottom padding for mobile */}
      <div className="h-20 lg:hidden" />
    </div>
  );
}
